import React, { useCallback, useMemo, useState } from 'react'
import { Button, Input, InputNumber, Table, Tooltip, Tag, message } from 'antd'
import { ExclamationCircleTwoTone, UndoOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { stockTakingService } from '@services/operator/stockTakingService'
import StickyActionBar from './StickyActionBar'
import CountEntriesTable from './CountEntriesTable'
import { useLeaveProtection } from '../hooks/useLeaveProtection'

/**
 * Spreadsheet 模式的盤點項目編輯器。
 *
 * State owned here:
 *   items             — 目前頁面載入的 rows（由 parent 或 this 元件呼叫 service 取得）
 *   pagination        — server-driven 分頁
 *   dirtyMap          — Map<itemId, { counted_quantity, note }>
 *   saveErrors        — Map<itemId, { message, interpolations? }>
 *   showOnlyDirty     — boolean
 *   savingBatch       — boolean
 *
 * Props:
 *   stockTakingId     — string
 *   isEditable        — boolean (false when stock_taking confirmed; disables inputs)
 *   onDirtyChange    — (count) => void  當 dirtyMap.size 變動時回報給 parent（用於外部 action 的 dirty gate）
 */
export default function ItemsSpreadsheet({ stockTakingId, isEditable, onDirtyChange }) {
  const { t } = useTranslation()

  const [items, setItems] = useState([])
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, hasMore: false })
  const [loading, setLoading] = useState(false)
  const [dirtyMap, setDirtyMap] = useState(() => new Map())
  const [saveErrors, setSaveErrors] = useState(() => new Map())
  const [showOnlyDirty, setShowOnlyDirty] = useState(false)
  const [savingBatch, setSavingBatch] = useState(false)

  // Notify parent of dirty-count changes so stock_taking-level actions
  // (close / zero-out / confirm-all) can be gated on "no unsaved edits".
  React.useEffect(() => {
    onDirtyChange?.(dirtyMap.size)
  }, [dirtyMap, onDirtyChange])

  // ---- data load ----
  // Note: backend paginates with `has_more` (not absolute `total`). We mirror the
  // existing Show.jsx pattern: synthesize a virtual total so AntD pagination can
  // render a Next button when `has_more` is true.
  const loadItems = useCallback(async (page, pageSize) => {
    setLoading(true)
    try {
      const response = await stockTakingService.getItems(stockTakingId, { page, page_size: pageSize })
      if (response.success) {
        setItems(response.data || [])
        setPagination({
          current: page,
          pageSize,
          hasMore: response.has_more || false
        })
      } else {
        message.error(response.message || t('common.error.load_failed'))
      }
    } catch (err) {
      console.error('Load items error:', err)
      message.error(t('common.error.unknown_error'))
    } finally {
      setLoading(false)
    }
  }, [stockTakingId, t])

  React.useEffect(() => {
    loadItems(1, 20)
  }, [loadItems])

  // ---- dirty helpers ----
  const getEffective = useCallback((row) => {
    const dirty = dirtyMap.get(row.id)
    if (!dirty) return { counted_quantity: row.counted_quantity, note: row.note }
    return dirty
  }, [dirtyMap])

  const isEqualToOriginal = (row, entry) => {
    const norm = (v) => (v === '' ? null : v)
    return norm(entry.counted_quantity) === norm(row.counted_quantity)
        && norm(entry.note) === norm(row.note)
  }

  const setRowField = (row, field, value) => {
    setDirtyMap((prev) => {
      const next = new Map(prev)
      const current = next.get(row.id) || { counted_quantity: row.counted_quantity, note: row.note }
      const updated = { ...current, [field]: value }
      if (isEqualToOriginal(row, updated)) {
        next.delete(row.id)
      } else {
        next.set(row.id, updated)
      }
      return next
    })
    // a re-edit clears any previous save error on this row
    setSaveErrors((prev) => {
      if (!prev.has(row.id)) return prev
      const next = new Map(prev)
      next.delete(row.id)
      return next
    })
  }

  const revertRow = (id) => {
    setDirtyMap((prev) => {
      if (!prev.has(id)) return prev
      const next = new Map(prev)
      next.delete(id)
      return next
    })
    setSaveErrors((prev) => {
      if (!prev.has(id)) return prev
      const next = new Map(prev)
      next.delete(id)
      return next
    })
  }

  const discardAll = () => {
    setDirtyMap(new Map())
    setSaveErrors(new Map())
  }

  // ---- view filter ----
  const visibleRows = useMemo(() => {
    if (!showOnlyDirty) return items
    // Only dirty rows. If the dirty row is not on the currently-loaded page,
    // we still have the original snapshot: derive from items by filtering id ∈ dirtyMap.
    // (Cross-page: Task 8 extends this to fetch dirty ids not in `items`.)
    return items.filter((row) => dirtyMap.has(row.id))
  }, [items, dirtyMap, showOnlyDirty])

  const columns = useMemo(() => ([
    // 後端 #items action 回傳扁平欄位（sku / product_name），非 nested inventory.product.*
    { title: 'SKU', dataIndex: 'sku', key: 'sku', width: 160 },
    { title: t('shared.stock_takings.show.product_name'), dataIndex: 'product_name', key: 'name' },
    { title: t('shared.stock_takings.show.expected_quantity'), dataIndex: 'expected_quantity', key: 'expected', width: 100, align: 'right' },
    {
      title: t('shared.stock_takings.show.counted_quantity'),
      key: 'counted',
      width: 140,
      render: (_, row) => {
        const { counted_quantity } = getEffective(row)
        return (
          <InputNumber
            min={0}
            step={1}
            precision={0}
            value={counted_quantity}
            onChange={(v) => setRowField(row, 'counted_quantity', v)}
            disabled={!isEditable || savingBatch}
            style={{ width: '100%' }}
            controls={false}
            placeholder="—"
          />
        )
      }
    },
    // dynamic 模式 App 會覆盤同一 item 多次，主表單列的 difference 只反映最新一輪；
    // 這個 column 讓使用者知道有幾筆 entry，引導展開列查看 CountEntriesTable 看完整歷史。
    {
      title: t('shared.stock_takings.show.count_entries_count'),
      dataIndex: 'count_entries_count',
      key: 'count_entries_count',
      width: 100,
      align: 'center',
      render: (count) => {
        if (!count) return '—'
        if (count > 1) {
          return (
            <Tooltip title={t('shared.stock_takings.show.count_entries_count_tooltip')}>
              <Tag color="orange">{count}</Tag>
            </Tooltip>
          )
        }
        return count
      }
    },
    {
      title: t('shared.stock_takings.show.note'),
      key: 'note',
      render: (_, row) => {
        const { note } = getEffective(row)
        return (
          <Input
            value={note ?? ''}
            onChange={(e) => setRowField(row, 'note', e.target.value || null)}
            disabled={!isEditable || savingBatch}
            placeholder={t('shared.stock_takings.shelf.note_placeholder')}
          />
        )
      }
    },
    {
      title: '',
      key: 'diff',
      width: 120,
      render: (_, row) => {
        const dirty = dirtyMap.get(row.id)
        const error = saveErrors.get(row.id)
        if (!dirty) return null
        const fromVal = row.counted_quantity ?? '—'
        const toVal = dirty.counted_quantity ?? '—'
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {error ? (
              <Tooltip title={t(`shared.stock_takings.show.spreadsheet.errors.${error.message}`, error.interpolations || {})}>
                <ExclamationCircleTwoTone twoToneColor="#ff4d4f" />
              </Tooltip>
            ) : (
              <Tooltip title={t('shared.stock_takings.show.spreadsheet.diff_tooltip', { from: fromVal, to: toVal })}>
                <Tag color="gold">•</Tag>
              </Tooltip>
            )}
            <Button
              type="link"
              icon={<UndoOutlined />}
              size="small"
              onClick={() => revertRow(row.id)}
              disabled={savingBatch}
            >
              {t('shared.stock_takings.show.spreadsheet.revert_this')}
            </Button>
          </div>
        )
      }
    }
  ]), [t, dirtyMap, saveErrors, isEditable, savingBatch, getEffective])

  // ---- batch save (filled in Task 8) ----
  const saveAll = async () => {
    if (dirtyMap.size === 0) return true

    setSavingBatch(true)
    const payloads = Array.from(dirtyMap.entries()).map(([id, entry]) => ({
      id,
      counted_quantity: entry.counted_quantity,
      note: entry.note
    }))

    try {
      const response = await stockTakingService.batchUpdateItems(stockTakingId, payloads)
      if (!response.success) {
        message.error(response.message || t('common.error.save_failed'))
        return false
      }
      const results = response.data?.results || []
      const successCount = results.filter((r) => r.success).length
      const failed = results.filter((r) => !r.success)

      // Prune dirty map: success rows leave; failed rows stay with updated saveErrors
      setDirtyMap((prev) => {
        const next = new Map(prev)
        results.forEach((r) => {
          if (r.success) next.delete(r.id)
        })
        return next
      })
      setSaveErrors(() => {
        const next = new Map()
        failed.forEach((r) => {
          next.set(r.id, { message: r.message, interpolations: r.interpolations })
        })
        return next
      })

      // Reload current page so row originals reflect DB truth.
      // While the "只看已修改" filter is on, still reload to refresh any still-visible rows;
      // successful rows will disappear from visibleRows automatically since dirtyMap shrank.
      await loadItems(pagination.current, pagination.pageSize)

      if (failed.length === 0) {
        message.success(t('shared.stock_takings.show.spreadsheet.save_success', { count: successCount }))
      } else {
        message.warning(t('shared.stock_takings.show.spreadsheet.save_partial', {
          success: successCount,
          failed: failed.length
        }))
      }

      return failed.length === 0
    } catch (err) {
      console.error('Batch save error:', err)
      message.error(t('common.error.unknown_error'))
      return false
    } finally {
      setSavingBatch(false)
    }
  }

  const onPageChange = (page, pageSize) => {
    if (showOnlyDirty) return // page navigation disabled while filter is on
    loadItems(page, pageSize)
  }

  // ---- leave protection ----
  useLeaveProtection(dirtyMap.size, async () => {
    const ok = await saveAll()
    return ok === true
  })

  return (
    <>
      {isEditable && (
        <div style={{ marginBottom: 8 }}>
          <Button
            type={showOnlyDirty ? 'primary' : 'default'}
            onClick={() => setShowOnlyDirty((s) => !s)}
            disabled={dirtyMap.size === 0 && !showOnlyDirty}
          >
            {t('shared.stock_takings.show.spreadsheet.only_dirty', { count: dirtyMap.size })}
          </Button>
        </div>
      )}

      <Table
        rowKey="id"
        dataSource={visibleRows}
        columns={columns}
        loading={loading}
        expandable={{
          // Lazy-load：展開時才 fetch 該 item 的 count entries 歷史
          expandedRowRender: (row) => (
            <CountEntriesTable stockTakingId={stockTakingId} itemId={row.id} />
          )
        }}
        pagination={showOnlyDirty ? false : {
          current: pagination.current,
          pageSize: pagination.pageSize,
          // mirror Show.jsx's virtual-total trick so the Next button appears when has_more
          total: pagination.hasMore
            ? (pagination.current * pagination.pageSize) + 1
            : pagination.current * pagination.pageSize,
          onChange: onPageChange,
          showSizeChanger: false
        }}
        locale={{
          emptyText: showOnlyDirty
            ? t('shared.stock_takings.show.spreadsheet.no_dirty')
            : undefined
        }}
      />

      {isEditable && (
        <StickyActionBar
          dirtyCount={dirtyMap.size}
          failedCount={saveErrors.size}
          saving={savingBatch}
          onSaveAll={saveAll}
          onDiscardAll={discardAll}
        />
      )}
    </>
  )
}
